/** One uploaded file in the upload dialog: its text status (announced as it changes) and the "Add as material" form. */

import { useState } from 'react';
import { FileText } from 'lucide-react';

import { materialInTitleMax } from '@/api/generated/schemas';
import { useAddMaterial } from '@/api/queries/materials';
import type { CatalogFileOut, MaterialAudience, MaterialType, TextStatus } from '@/api/types/materials';
import { bannerError, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { formatBytes } from '@/ui/Attachments';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FormError } from '@/ui/StateCard';

import { AudienceField, MaterialTypeField } from './MaterialFields';
import type { MaterialWriter } from './MaterialDialogs';
import { materialValid, titleFromFilename } from './materialList';

const STATUS_TEXT: Record<TextStatus, string> = {
  pending: 'Reading the text…',
  ready: 'Text ready',
  unreadable: 'No readable text. It can still be added, but search and the AI will not read it.',
};

export function UploadedFileForm({
  file,
  status,
  writer,
  onAdded,
}: {
  file: CatalogFileOut;
  status: TextStatus;
  writer: MaterialWriter;
  onAdded: () => void;
}) {
  const add = useAddMaterial(writer.assetId);
  const [title, setTitle] = useState(titleFromFilename(file.filename));
  const [type, setType] = useState<MaterialType | ''>('');
  const [audience, setAudience] = useState<MaterialAudience | ''>('');
  const valid = materialValid({ title, material_type: type, audience });
  const id = `upload-${file.id}`;
  const titleError = serverFieldErrors(add.error).title;

  function submit() {
    if (!valid || type === '' || audience === '') return;
    add.mutate(
      { revision: writer.revision, file_id: file.id, title: title.trim(), material_type: type, audience },
      { onSuccess: (answer) => (writer.saw(answer), onAdded()) },
    );
  }

  return (
    <section className="card material-upload" aria-labelledby={`${id}-name`}>
      <div className="card-body stack-md">
        <div className="material-upload-head">
          <FileText aria-hidden="true" />
          <b id={`${id}-name`}>{file.filename}</b>
          <span className="hint">{formatBytes(file.size_bytes)}</span>
        </div>
        <p className={status === 'unreadable' ? 'attach-error' : 'hint'} aria-live="polite">
          {STATUS_TEXT[status]}
        </p>
        <FormError error={bannerError(add.error, ['title'])} />
        <div className="field">
          <label htmlFor={`${id}-title`}>Title</label>
          <input id={`${id}-title`} type="text" maxLength={materialInTitleMax} value={title} {...fieldA11y(`${id}-title`, titleError)} onChange={(e) => setTitle(e.target.value)} />
          <FieldError inputId={`${id}-title`} message={titleError} />
        </div>
        <MaterialTypeField id={`${id}-type`} value={type} onChange={setType} />
        <AudienceField name={`${id}-audience`} value={audience} onChange={setAudience} />
        <div>
          <ActionButton
            type="button"
            className="btn btn-primary btn-sm"
            state={buttonState(add)}
            label={`Add ${file.filename} as material`}
            pendingLabel="Adding…"
            doneLabel="Added"
            disabled={!valid}
            onClick={submit}
          />
        </div>
      </div>
    </section>
  );
}
